/**
 * Application entry point.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './lib/auth';
import { LiveProvider } from './lib/live';
import { I18nProvider } from './lib/i18n';
import { ToastProvider } from './components/ui';
import { startAutoDrain } from './lib/outbox';
import { sendOutboxItem } from './lib/api';
import './styles.css';

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <I18nProvider>
      <AuthProvider>
        <LiveProvider>
          <ToastProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ToastProvider>
        </LiveProvider>
      </AuthProvider>
    </I18nProvider>
  </StrictMode>,
);

// Remove the pre-React boot screen once the first paint has happened.
requestAnimationFrame(() => {
  document.getElementById('boot')?.remove();
});

/**
 * Start draining the offline outbox.
 *
 * Deliberately outside React: the queue must keep flushing while the user
 * navigates, and it should not be tied to a component's lifetime. It re-runs on
 * reconnect, when the tab becomes visible again, and on a slow timer.
 */
startAutoDrain(sendOutboxItem);

/**
 * Service worker.
 *
 * Registered after load so it never competes with the first paint for
 * bandwidth. When a new version is waiting, the app is told rather than
 * silently swapped — reloading mid-order would lose a cart.
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).then((registration) => {
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            window.dispatchEvent(new CustomEvent('gd:update-available'));
          }
        });
      });
    }).catch(() => {
      // A failed registration must not break the app — it just means no
      // offline shell on this device.
    });

    // A notification tap posts a route here.
    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event.data as { type?: string; path?: string } | undefined;
      if (data?.type === 'NAVIGATE' && data.path) {
        window.dispatchEvent(new CustomEvent('gd:navigate', { detail: data.path }));
      }
    });
  });
}
